<template>
  <BittsModal
    v-model="isVisible"
    :width="600"
    :use-mask-to-close="true"
    container-class="c-replace-open-data-partner-dialog"
    title="Replace Deprecated Partners"
    :description="modalDescription"
    secondary-button-text="Cancel"
    :primary-button-text="primaryButtonText"
    :disabled="isPrimaryDisabled"
    :action-loading="isConverting"
    @action="handleReplace"
    @closed="handleClosed"
  >
    <div class="c-replace-open-data-partner-dialog__content">
      <BillingCTA
        v-if="showBillingCta"
        :message="billingCtaMessage"
        :description="billingCtaDescription"
        :button-text="billingCtaButtonText"
        @cta-button-clicked="handleBillingCtaClick"
      />
      <BittsAlert
        v-if="hasAvailableOpenDataQuota"
        color="info"
        message="What happens?"
      >
        <template #body>
          <ul class="c-replace-open-data-partner-dialog__alert-list">
            <li data-testid="replace-partner-deletion">
              {{ deletionText }}
            </li>
            <li data-testid="replace-partner-replacement">
              {{ replacementText }}
            </li>
            <li data-testid="replace-partner-quota-usage">
              {{ quotaUsageText }}
            </li>
            <li data-testid="replace-partner-slot-lock">
              {{ slotLockText }}
            </li>
          </ul>
        </template>
      </BittsAlert>
      <ListContainer
        data-testid="replace-partner-bulk-list"
        :list="partnerRows"
        :get-key="(item) => item.id"
        :has-divider="() => false"
        :on-click-item="handleRowClick"
      >
        <template #default="{ item }">
          <div
            class="c-replace-open-data-partner-dialog__row"
            :class="{
              'cursor-not-allowed': isRowDisabled(item.id),
            }"
            :data-testid="`replace-partner-row-${item.id}`"
          >
            <BittsCheckbox
              :checked="selectedIds.has(item.id)"
              :disabled="isRowDisabled(item.id)"
              :aria-label="`Select ${item.name}`"
              data-testid="replace-partner-checkbox"
              @input="
                (checked: boolean) => handleCheckboxChange(item.id, checked)
              "
              @click.stop
            />
            <BittsAvatar
              class="ml-16"
              :org="item"
              size="x-small"
              :show-initials="true"
            />
            <span
              class="c-replace-open-data-partner-dialog__row-name ml-8"
              :class="{
                'text-neutral-text-weak': isRowDisabled(item.id),
              }"
            >
              {{ item.name }}
            </span>
            <FontAwesomeIcon
              :icon="['fak', 'managed-offline-partner']"
              class="c-replace-open-data-partner-dialog__row-icon ml-6"
              :class="{
                'opacity-50': isRowDisabled(item.id),
              }"
            />
          </div>
        </template>
      </ListContainer>
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatar,
  BittsCheckbox,
  BittsModal,
} from '@crossbeam/bitts';
import { EVENT_SITES, PARTNER_CTAS } from '@crossbeam/itly';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import BillingCTA from '@/components/billing/BillingCTA.vue';
import ListContainer from '@/components/ListContainer.vue';

import useBilling from '@/composables/useBilling';
import useManagedOfflinePartner, {
  OPEN_DATA_SWAPS_EXHAUSTED_DESCRIPTION,
  OPEN_DATA_SWAPS_EXHAUSTED_MESSAGE,
} from '@/composables/useManagedOfflinePartner';
import {
  OPEN_DATA_PARTNERS_LIST_ROUTE,
  useOpenDataPartnerPreparing,
} from '@/composables/useOpenDataPartnerPreparing';
import { captureException } from '@/errors';
import { useBillingStore, useFlashesStore, usePartnersStore } from '@/stores';
import type { BillingInteraction } from '@/types/billing';
import type { Partner } from '@/types/partners';

const props = defineProps<{
  preselectedPartnerOrgId?: number;
}>();

const emit = defineEmits<{
  closed: [];
}>();

const isVisible = defineModel<boolean>({ default: false });

const route = useRoute();
const router = useRouter();
const { handleBillingInteraction } = useBilling();
const { convertManagedOfflinePartnersToOpenData } = useManagedOfflinePartner();
const { addErrorFlash } = useFlashesStore();
const { showOpenDataPartnersPreparing } = useOpenDataPartnerPreparing();
const {
  isFreeTier,
  isEnterpriseTier,
  availableOpenDataQuota,
  isSelfServeLegacy,
  openDataPartnersQuota,
  openDataPartnerOrgsUsed,
  remainingLifetimeAdditions,
} = storeToRefs(useBillingStore());
const { replaceableManagedOfflinePartners } = storeToRefs(usePartnersStore());

function initialSelection() {
  const preselected = props.preselectedPartnerOrgId;
  if (preselected != null) return new Set([preselected]);
  // A single partner leaves nothing to choose between, so check it for them.
  const partners = replaceableManagedOfflinePartners.value;
  if (partners.length === 1) return new Set(partners.map(({ id }) => id));
  return new Set<number>();
}

// Parents mount this with v-if plus v-model="true", so the visibility watch
// below never fires for the first open - the ref has to be seeded here too.
const selectedIds = ref(initialSelection());
const hasTouchedSelection = ref(false);
const isConverting = ref(false);

const modalDescription =
  "Managed Offline Partners are no longer maintained.\nSelect the partners you'd like to replace with Open Data Partners.";

const orderedPartners = computed(() => {
  const partners = replaceableManagedOfflinePartners.value;
  const preselected = props.preselectedPartnerOrgId;
  if (preselected == null) return partners;
  const preselectedPartner = partners.find(
    (partner) => partner.id === preselected,
  );
  if (!preselectedPartner) return partners;
  return [
    preselectedPartner,
    ...partners.filter((partner) => partner.id !== preselected),
  ];
});

// Frozen while converting: the dialog is still on screen for the request and
// the tick before it closes, and the rows must not shift under the user.
const frozenPartners = ref<Partner[] | null>(null);
const partnerRows = computed(
  () => frozenPartners.value ?? orderedPartners.value,
);

const selectedCount = computed(() => selectedIds.value.size);

// The lifetime additions cap can tighten how many replacements are allowed
// below the concurrent quota, so gate on whichever bound is lower.
const effectiveRemainingQuota = computed(() => {
  const remaining = remainingLifetimeAdditions.value;
  if (remaining === null) return availableOpenDataQuota.value;
  return Math.min(availableOpenDataQuota.value, remaining);
});

const hasAvailableOpenDataQuota = computed(
  () => effectiveRemainingQuota.value > 0,
);

const hasReachedQuota = computed(
  () => selectedCount.value >= effectiveRemainingQuota.value,
);

const quotaUsageText = computed(() => {
  if (!selectedCount.value) {
    const available = effectiveRemainingQuota.value;
    return `You have ${available} ${pluralize(available, 'slot')} available.`;
  }
  const quota = openDataPartnersQuota.value ?? 0;
  const totalUsed = (openDataPartnerOrgsUsed.value ?? 0) + selectedCount.value;
  return `Will use ${totalUsed} of your ${quota} Open Data Partner ${pluralize(quota, 'slot')}.`;
});

const isReplacingMultiple = computed(() => selectedCount.value > 1);

const deletionText = computed(() =>
  isReplacingMultiple.value
    ? 'Deletes your Managed Offline Partners, their reports and shared lists.'
    : 'Deletes your Managed Offline Partner, its reports and shared lists.',
);

const replacementText = computed(() =>
  isReplacingMultiple.value
    ? 'Adds up-to-date Open Data Partners for the same companies.'
    : 'Adds an up-to-date Open Data Partner for the same company.',
);

const slotLockText = computed(() => {
  if (!selectedCount.value)
    return "Once used, a slot can't be switched to a different Open Data Partner.";
  return isReplacingMultiple.value
    ? "You won't be able to switch these slots to other Open Data Partners."
    : "You won't be able to switch this slot to another Open Data Partner later.";
});

// True when the quota stops the current selection outright, as opposed to
// merely capping further ones.
const isQuotaBlocking = computed(
  () =>
    effectiveRemainingQuota.value <= 0 ||
    selectedCount.value > effectiveRemainingQuota.value,
);

const hasUnselectedPartners = computed(
  () => partnerRows.value.length > selectedCount.value,
);

// Not shown just because a preselected partner fills the last slot - that
// replacement is still allowed, and the upsell abandons it to start billing.
// Once the user selects up to the cap themselves it explains the greyed rows.
const showBillingCta = computed(
  () =>
    isQuotaBlocking.value ||
    (hasReachedQuota.value &&
      hasTouchedSelection.value &&
      hasUnselectedPartners.value),
);

const primaryButtonText = computed(() => {
  if (!selectedCount.value) return 'Replace Selected Partners';
  return `Replace ${selectedCount.value} ${pluralize(selectedCount.value, 'Partner')}`;
});

// A preselected partner is checked without passing through isRowDisabled, so
// the quota has to be re-checked here or an org with none left could convert.
const isPrimaryDisabled = computed(
  () =>
    !selectedCount.value || selectedCount.value > effectiveRemainingQuota.value,
);

// Upgrading never grants more lifetime additions, so the swap-exhausted copy (and a Talk to
// Sales action) only apply when the additions are actually spent. Replace flows have no free
// re-add exemption, so exhaustion is either zero remaining or a selection over the cap.
const hasExhaustedLifetimeAdditions = computed(() => {
  const remaining = remainingLifetimeAdditions.value;
  if (remaining === null) return false;
  return remaining === 0 || selectedCount.value > remaining;
});

// Only free tier orgs get lifetime swaps; a paid org that hits the cap sees the
// standard limit copy, not swap language.
const showsSwapExhaustedCopy = computed(
  () => hasExhaustedLifetimeAdditions.value && isFreeTier.value,
);

const billingCtaMessage = computed(() =>
  showsSwapExhaustedCopy.value
    ? OPEN_DATA_SWAPS_EXHAUSTED_MESSAGE
    : "You've reached your Open Data Partner limit",
);

const billingCtaDescription = computed(() => {
  if (showsSwapExhaustedCopy.value)
    return OPEN_DATA_SWAPS_EXHAUSTED_DESCRIPTION;
  if (isFreeTier.value || isSelfServeLegacy.value)
    return 'Upgrade to add more Open Data Partners.';
  if (isEnterpriseTier.value)
    return 'Add a Data Pack to access more Open Data Partners.';
  return 'Contact Sales to add more Open Data Partners.';
});

const billingCtaButtonText = computed(() =>
  !hasExhaustedLifetimeAdditions.value &&
  (isFreeTier.value || isSelfServeLegacy.value)
    ? 'Upgrade'
    : 'Talk to Sales',
);

const billingInteraction = computed(
  (): BillingInteraction => ({
    cta: PARTNER_CTAS.MANAGED_OFFLINE_PARTNERS,
    event_site: EVENT_SITES.MANAGED_OFFLINE_PARTNERS_MODAL,
    isPlatformUpgrade: isSelfServeLegacy.value,
    talkToSalesReason:
      !hasExhaustedLifetimeAdditions.value &&
      (isFreeTier.value || isSelfServeLegacy.value)
        ? undefined
        : 'More Open Data Partners',
  }),
);

function isRowDisabled(id: number) {
  if (selectedIds.value.has(id)) return false;
  return hasReachedQuota.value;
}

function handleCheckboxChange(id: number, checked: boolean) {
  if (checked && isRowDisabled(id)) return;
  hasTouchedSelection.value = true;
  const updated = new Set(selectedIds.value);
  if (checked) updated.add(id);
  else updated.delete(id);
  selectedIds.value = updated;
}

function handleRowClick(item: Partner) {
  if (isRowDisabled(item.id)) return;
  handleCheckboxChange(item.id, !selectedIds.value.has(item.id));
}

async function handleReplace() {
  const ids = [...selectedIds.value];
  if (!ids.length) return;

  // Read the prop once: a parent can clear it while the request is in flight,
  // and the backdrop check below has to know where the user started from.
  const preselectedPartnerOrgId = props.preselectedPartnerOrgId;
  frozenPartners.value = orderedPartners.value;
  isConverting.value = true;
  try {
    const { convertedPairs, failureCount, refreshStores } =
      await convertManagedOfflinePartnersToOpenData(ids);

    const convertedCount = convertedPairs.length;
    if (failureCount) {
      addErrorFlash({
        message: `${failureCount} ${pluralize(failureCount, 'partner')} could not be replaced`,
      });
    }
    if (!convertedCount) return;

    // The page behind this dialog may be the partnership we just deleted, so it
    // cannot stay as the backdrop for the confirmation dialog.
    const isOnReplacedPartnerPage =
      route.name === 'partner_details' &&
      route.params.partner_org_id === String(preselectedPartnerOrgId) &&
      convertedPairs.some(
        (pair) => pair.mop_org_id === preselectedPartnerOrgId,
      );

    // Close first: the refresh empties the list this dialog is showing, and
    // watching the rows disappear before it closes reads as a glitch.
    isVisible.value = false;
    await nextTick();
    if (isOnReplacedPartnerPage) {
      await router.push(OPEN_DATA_PARTNERS_LIST_ROUTE);
    }

    showOpenDataPartnersPreparing({
      count: convertedCount,
      partnerOrgId: convertedPairs[0]?.odp_org_id ?? null,
      refreshPromise: refreshStores?.() ?? null,
    });
  } catch (err) {
    captureException(err);
    addErrorFlash({ message: 'Something went wrong. Please try again.' });
  } finally {
    isConverting.value = false;
    frozenPartners.value = null;
  }
}

async function handleBillingCtaClick() {
  isVisible.value = false;
  await handleBillingInteraction(billingInteraction.value);
}

function handleClosed() {
  selectedIds.value = initialSelection();
  hasTouchedSelection.value = false;
  emit('closed');
}

watch(isVisible, (visible) => {
  if (!visible) return;
  selectedIds.value = initialSelection();
  hasTouchedSelection.value = false;
});

// The list can load after mounting, which the single-partner selection needs -
// re-seed until the user has made a choice of their own.
watch(replaceableManagedOfflinePartners, () => {
  if (hasTouchedSelection.value || selectedIds.value.size) return;
  selectedIds.value = initialSelection();
});
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";

:deep(.o-bitts-modal__description) {
  @apply text-base! whitespace-pre-line;
}

:deep(.o-bitts-modal__content) {
  @apply px-0!;
}

.c-replace-open-data-partner-dialog__content {
  @apply flex flex-col gap-16;
}

.c-replace-open-data-partner-dialog__alert-list {
  @apply list-disc pl-20 m-0 text-base text-info-text leading-[1.4];
}

.c-replace-open-data-partner-dialog__row {
  @apply flex items-center px-16 py-16 cursor-pointer;
}

.c-replace-open-data-partner-dialog__row-name {
  @apply font-bold text-base text-neutral-text-strong leading-[1.5];
}

.c-replace-open-data-partner-dialog__row-icon {
  @apply text-12 text-warning-accent;
}
</style>
